/**
 * Aura API helpers (Phase 3).
 * UI → Helpers → API.
 */

export async function getAuraCategories() {
  const res = await fetch('/api/aura/categories', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch aura categories');
  return res.json();
}

export async function createAura({ targetId, targetType, postId, commentId, messageId, messageType, categoryId, points }) {
  const body = {
    targetId,
    targetType: targetType || 'user',
    categoryId,
    points: points || 1,
  };
  if (messageId != null && messageType) {
    body.messageId = messageId;
    body.messageType = messageType;
  } else if (commentId != null) {
    body.commentId = commentId;
  } else if (postId != null) {
    body.postId = postId;
  }

  const res = await fetch('/api/aura', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to give aura');
  }
  return res.json();
}

export async function getAuraSphereData(userId) {
  const params = new URLSearchParams({ user_id: userId });
  const res = await fetch(`/api/aura/sphere?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch aura sphere');
  return res.json();
}

export async function getAuraTotals(userId, timeframe = 'week') {
  const params = new URLSearchParams({ timeframe });
  if (userId) params.set('user_id', userId);
  const res = await fetch(`/api/aura/totals?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch aura totals');
  return res.json();
}

export async function getUserDailyPointStats() {
  const res = await fetch('/api/aura/daily-stats', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch daily stats');
  return res.json();
}

/**
 * Fetch aura counts for messages. items: [{ type: 'dm_message'|'group_message'|'club_message', id: number }]
 * Returns { dm_message: { 1: 5 }, group_message: { 5: 3 }, ... }
 */
export async function getAuraMessageCounts(items) {
  if (!items?.length) return {};
  const res = await fetch('/api/aura/message-counts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
    credentials: 'include',
  });
  if (!res.ok) return {};
  return res.json();
}

/**
 * Returns total aura points across all categories (received only).
 * Users only gain aura when someone sends it to them; sending does not reduce aura.
 */
export function getTotalAuraAmount(auraTotals) {
  if (!auraTotals || typeof auraTotals !== 'object') return 0;
  let total = 0;
  for (const [, data] of Object.entries(auraTotals)) {
    const received = data.received_points?.reduce((s, p) => s + (p.points || 0), 0) || 0;
    total += received;
  }
  return total;
}

/**
 * Returns strength level for halo sizing: 'minimal' | 'small' | 'medium' | 'strong'
 * 0-5: minimal, 5-10: small, 10-25: medium, 25+: strong
 */
export function getAuraStrengthLevel(auraTotals) {
  const amount = getTotalAuraAmount(auraTotals);
  if (amount < 5) return 'minimal';
  if (amount < 10) return 'small';
  if (amount < 25) return 'medium';
  return 'strong';
}

const MAX_RINGS = 100;

/**
 * Returns array of { color } for halo rings.
 * One ring per aura point, capped at 100. Colors are interleaved so dominant
 * types don't form solid bands (e.g. 10 blue, 5 red, 5 green → B R G B R G ...).
 */
export function getAuraRingsData(auraTotals) {
  if (!auraTotals || typeof auraTotals !== 'object') return [];
  const byColor = [];
  for (const [, data] of Object.entries(auraTotals)) {
    if (!data?.category_color) continue;
    const received = data.received_points?.reduce((s, p) => s + (p.points || 0), 0) || 0;
    const total = received;
    if (total > 0) byColor.push({ color: data.category_color, count: total });
  }
  if (byColor.length === 0) return [];

  const total = byColor.reduce((s, c) => s + c.count, 0);
  const totalRings = Math.min(total, MAX_RINGS);
  if (totalRings === 0) return [];

  const withPositions = [];
  for (const { color, count } of byColor) {
    const take = Math.max(1, Math.round((count / total) * totalRings));
    for (let i = 0; i < take; i++) {
      const pos = (i + 0.5) * totalRings / take;
      withPositions.push({ color, pos });
    }
  }

  return withPositions
    .sort((a, b) => a.pos - b.pos)
    .slice(0, totalRings)
    .map(({ color }) => ({ color }));
}

/**
 * Returns the hex color of the aura category with the highest total (received + sent).
 * Used for profile halo color. Fallback: #6B8CFF (ether).
 */
export function getDominantAuraColor(auraTotals) {
  if (!auraTotals || typeof auraTotals !== 'object') return '#6B8CFF';
  let maxTotal = 0;
  let dominantColor = '#6B8CFF';
  for (const [, data] of Object.entries(auraTotals)) {
    if (!data?.category_color) continue;
    const received = data.received_points?.reduce((s, p) => s + (p.points || 0), 0) || 0;
    const total = received;
    if (total > maxTotal) {
      maxTotal = total;
      dominantColor = data.category_color;
    }
  }
  return dominantColor;
}
